import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Alert, Collapse, Space, Typography } from "antd";
import { Button, Pill, Skeleton } from "@libertify/ui";
import { useMfaFactors } from "hooks/useMfaFactors";
import { useMfaEnrollment } from "../hooks/useMfaEnrollment";
import { findMfaFactor } from "../utils";
import type { MfaFactor, MfaFactorStatus } from "../types";
import { TotpFactorCard } from "./TotpFactorCard";
import { SmsFactorCard } from "./SmsFactorCard";

// Renders the two MFA factor panels inline in Profile settings — no
// separate page/modal indirection. Every panel header always shows an
// Enabled/Not enabled pill (plus Preferred when applicable), collapsed or
// not. Panels start collapsed and close themselves again after a
// successful setup/disable.
export const TwoFactorAuthSection = () => {
  const t = useTranslate();
  const { factors, isLoading, isError, refetch } = useMfaFactors(true);
  const { startTotp, confirmTotp, startSms, confirmSms, disableFactor } =
    useMfaEnrollment();
  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const collapse = (factor: MfaFactor) =>
    setActiveKeys((keys) => keys.filter((key) => key !== factor));

  const renderHeader = (titleKey: string, factor: MfaFactorStatus) => (
    <Space>
      <Typography.Text strong>{t(titleKey)}</Typography.Text>
      {factor.enabled ? (
        <Pill tone="success">{t("pages.settings.security.enabled")}</Pill>
      ) : (
        <Pill tone="neutral">{t("pages.settings.security.notEnabled")}</Pill>
      )}
    </Space>
  );

  if (isLoading) {
    return <Skeleton variant="card" />;
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("pages.settings.security.loadError")}
        action={
          <Button
            variant="secondary"
            onClick={() => {
              refetch().catch((error) => {
                console.error("Failed to refresh MFA factors", error);
              });
            }}
          >
            {t("buttons.refresh")}
          </Button>
        }
      />
    );
  }

  return (
    <Collapse
      activeKey={activeKeys}
      onChange={(keys) => setActiveKeys(Array.isArray(keys) ? keys : [keys])}
      // Unmount collapsed panels so an abandoned in-progress setup (e.g.
      // TOTP QR/verify step) resets back to the "Set up" button instead of
      // resuming mid-flow next time the panel is expanded.
      destroyOnHidden
      items={[
        {
          key: "Totp",
          label: renderHeader(
            "pages.settings.security.factors.Totp.title",
            findMfaFactor(factors, "Totp")
          ),
          children: (
            <TotpFactorCard
              factor={findMfaFactor(factors, "Totp")}
              startTotp={startTotp}
              confirmTotp={confirmTotp}
              disableFactor={disableFactor}
              onCollapse={() => collapse("Totp")}
            />
          ),
        },
        {
          key: "Sms",
          label: renderHeader(
            "pages.settings.security.factors.Sms.title",
            findMfaFactor(factors, "Sms")
          ),
          children: (
            <SmsFactorCard
              factor={findMfaFactor(factors, "Sms")}
              startSms={startSms}
              confirmSms={confirmSms}
              disableFactor={disableFactor}
              onCollapse={() => collapse("Sms")}
            />
          ),
        },
      ]}
    />
  );
};
